import React, { useState } from 'react';
import { Alert, App, Button, Input, Modal, Popconfirm, Space } from 'antd';

import { isNotDecidedByRuleError, isNotPendingError, isPendingIntake, reviewIntakeItem } from '../../services/records';
import { isRuleAccepted } from '../../utils/reviewRules';

/**
 * Triage for one intake submission: Accept or Reject, where the reader is
 * already looking at what was sent.
 *
 * A submission from someone without an account lands PENDING: out of the
 * list, counts, search, exports and every public view, and inert to the
 * collection's automations, until a person here decides it.
 *
 *   ACCEPT makes it a record like any other — it joins the list, and the
 *   automations see it once, as created. It cannot be taken back through
 *   History (the API refuses to revert an accept), so it is confirmed.
 *   REJECT deletes it — the ordinary delete, kept in History, where restoring
 *   it puts it back in this queue still waiting for review. Confirmed, with
 *   an optional reason kept beside the deletion.
 *
 * Anyone who can write the item may do either (`canEdit`): a writer can
 * already put anything on the collection by typing it, so vouching for a
 * stranger's words needs no more than that. Renders nothing unless the item
 * IS pending and the reader can act, so every surface can drop it in.
 *
 * A submission a public form's review RULE accepted (KPR-482: `intake.
 * decidedBy: 'rule'`) is a record nobody read. For the same people the bar
 * says so — which rule — and offers Send back to review (the API's `reopen`:
 * back to this queue, pending, out of the list again) and Reject… (deleted,
 * restorable from History). A person's accept is never offered either.
 *
 * `onReviewed(decision, result)` — 'accept' with the API's `{ item }`,
 * 'reject', 'reopen' with `{ item }`, or 'stale' when somebody else decided
 * it first.
 */
export default function IntakeReviewBar({ item, canEdit = false, onReviewed, className = '' }) {
	const { message } = App.useApp();
	const [busy, setBusy] = useState(null);
	const [rejectOpen, setRejectOpen] = useState(false);
	// A reopen the card's delete policy holds to a reason (KPR-482: the API
	// judges a reopen as a delete): asked only once the API says so.
	const [reopenReasonOpen, setReopenReasonOpen] = useState(false);
	const [reason, setReason] = useState('');

	const ruled = isRuleAccepted(item);
	if ((!isPendingIntake(item) && !ruled) || !canEdit) return null;

	const itemId = `${item?._id?.$oid || item?._id || ''}`;

	const decide = async (decision) => {
		setBusy(decision);
		try {
			const result = await reviewIntakeItem(itemId, decision, { reason });
			message.success(decision === 'accept'
				? 'Accepted. It is now an item of this collection like any other.'
				: decision === 'reopen'
					? 'Sent back to review. It waits in the queue, out of the list, until someone decides it.'
					: 'Rejected and deleted. It can be restored from History for 30 days.');
			setRejectOpen(false);
			setReopenReasonOpen(false);
			setReason('');
			onReviewed?.(decision, result);
		} catch (error) {
			const data = error?.response?.data || {};
			if (isNotPendingError(error) || isNotDecidedByRuleError(error)) {
				message.info('Somebody else has already reviewed this submission.');
				setRejectOpen(false);
				setReopenReasonOpen(false);
				onReviewed?.('stale', null);
			} else if (decision === 'reopen' && data.code === 'reason_required') {
				// The card keeps a reason with every record that leaves it.
				setReopenReasonOpen(true);
			} else if (decision === 'reopen' && data.code === 'item_undeletable') {
				// The API judges a reopen as a delete (the record leaves every
				// reader); its own words speak of deleting.
				message.error(error?.response?.status === 403
					? 'Only a manager can send a record of this card back to review.'
					: 'Records of this card stay once they are records, so this one cannot be sent back. Edit it instead.');
			} else {
				message.error(data.message || 'Could not review this submission.');
			}
		} finally {
			setBusy(null);
		}
	};

	const ruleName = item?.intake?.ruleName;
	const rejectModal = (
		<Modal
			open={rejectOpen}
			title="Reject this submission?"
			okText="Reject"
			okButtonProps={{ danger: true, 'data-action': 'intake-reject-confirm' }}
			confirmLoading={busy === 'reject'}
			onOk={() => decide('reject')}
			onCancel={() => setRejectOpen(false)}
			destroyOnClose
		>
			{ruled ? (
				<p>
					It is deleted. Its history is kept, and restoring it from History brings it back as it was —
					without anything the visitor sent privately, which is deleted for good.
				</p>
			) : (
				<p>
					It is deleted. Its history is kept, and restoring it from History brings it back here,
					still waiting for review — without anything the visitor sent privately, which is
					deleted for good.
				</p>
			)}
			<label className="intake-review__label" htmlFor="intake-reject-reason">Reason (optional)</label>
			<Input
				id="intake-reject-reason"
				value={reason}
				maxLength={500}
				placeholder="e.g. Spam, or a duplicate of an existing item"
				onChange={event => setReason(event.target.value)}
			/>
		</Modal>
	);

	const reopenModal = (
		<Modal
			open={reopenReasonOpen}
			title="Send it back to review?"
			okText="Send back"
			okButtonProps={{ disabled: !reason.trim(), 'data-action': 'intake-reopen-confirm' }}
			confirmLoading={busy === 'reopen'}
			onOk={() => decide('reopen')}
			onCancel={() => setReopenReasonOpen(false)}
			destroyOnClose
		>
			<p>
				It leaves the list and waits in the review queue. This card keeps a reason with every record that
				leaves it.
			</p>
			<label className="intake-review__label" htmlFor="intake-reopen-reason">Reason</label>
			<Input
				id="intake-reopen-reason"
				value={reason}
				maxLength={500}
				placeholder="e.g. Not checked — the rule was too broad"
				onChange={event => setReason(event.target.value)}
			/>
		</Modal>
	);

	if (ruled) {
		return (
			<>
				<Alert
					className={['intake-review', 'intake-review--ruled', className].filter(Boolean).join(' ')}
					type="info"
					showIcon
					message={ruleName ? `Accepted by the rule “${ruleName}”` : 'Accepted by a rule'}
					data-testid="intake-review-ruled"
					description={(
						<>
							<p className="intake-review__why">
								Nobody read it before it joined the collection: a public form’s rule matched its answers.
								If it should not be here, send it back to review or reject it.
							</p>
							<Space wrap className="intake-review__actions">
								<Popconfirm
									title="Send it back to review?"
									description="It leaves the list and waits in the review queue until someone accepts or rejects it."
									okText="Send back"
									onConfirm={() => decide('reopen')}
								>
									<Button loading={busy === 'reopen'} data-action="intake-reopen">
										Send back to review
									</Button>
								</Popconfirm>
								<Button danger onClick={() => setRejectOpen(true)} data-action="intake-reject">
									Reject…
								</Button>
							</Space>
						</>
					)}
				/>
				{rejectModal}
				{reopenModal}
			</>
		);
	}

	const via = item?.intake?.linkName
		? `Someone without an account sent this through the “${item.intake.linkName}” intake link.`
		: 'Someone without an account sent this through an intake link.';

	return (
		<>
			<Alert
				className={['intake-review', className].filter(Boolean).join(' ')}
				type="warning"
				showIcon
				message="Waiting for review"
				data-testid="intake-review-bar"
				description={(
					<>
						<p className="intake-review__why">
							{via} Nobody here has checked it — treat its links and addresses the way you would
							an email from a stranger. It stays out of the list, exports and public views until
							it is accepted.
						</p>
						<Space wrap className="intake-review__actions">
							<Popconfirm
								title="Accept this submission?"
								description="It becomes an item of this collection like any other, and the collection's automations run on it. Accepting cannot be undone from History."
								okText="Accept"
								onConfirm={() => decide('accept')}
							>
								<Button type="primary" loading={busy === 'accept'} data-action="intake-accept">
									Accept
								</Button>
							</Popconfirm>
							<Button danger onClick={() => setRejectOpen(true)} data-action="intake-reject">
								Reject…
							</Button>
						</Space>
					</>
				)}
			/>
			{rejectModal}
		</>
	);
}
